import {defineStore} from 'pinia';
import {ref, computed} from 'vue';
import {send} from "@/composables/apiClient";

export const useSubscriptionStore = defineStore('subscription', () => {
    const status = ref(JSON.parse(localStorage.getItem('subscriptionStatus') || 'null'));
    const lastFetched = ref(0);
    const modalDismissed = ref(false);

    const paymentStatus = computed(() => status.value?.payment_status ?? 'good');
    const isOverdue = computed(() => ['overdue', 'grace', 'suspended'].includes(paymentStatus.value));
    const isWarning = computed(() => paymentStatus.value === 'warning');
    const needsAttention = computed(() => paymentStatus.value !== 'good');
    const daysUntilSuspension = computed(() => status.value?.days_until_suspension ?? null);
    const overdueInvoiceCount = computed(() => status.value?.overdue_invoice_count ?? 0);
    const overdueTotal = computed(() => status.value?.overdue_total ?? 0);
    const expiredDevicesCount = computed(() => status.value?.expired_devices_count ?? 0);
    const expiringDevicesCount = computed(() => status.value?.expiring_devices_count ?? 0);

    const statusMessage = computed(() => {
        switch (paymentStatus.value) {
            case 'suspended':
                return 'Account suspended - services are paused until payment is received.';
            case 'grace': {
                const days = daysUntilSuspension.value;
                return `Grace period active - ${days} day(s) until service suspension.`;
            }
            case 'overdue': {
                const parts = [];
                if (overdueInvoiceCount.value > 0) parts.push(`${overdueInvoiceCount.value} overdue invoice(s)`);
                if (expiredDevicesCount.value > 0) parts.push(`${expiredDevicesCount.value} expired device(s)`);
                return `Payment overdue: ${parts.join(' and ')}. Please pay now.`;
            }
            case 'warning': {
                const parts = [];
                if (expiringDevicesCount.value > 0) parts.push(`${expiringDevicesCount.value} device(s) expiring soon`);
                if (status.value?.upcoming_invoice_count > 0) parts.push(`${status.value.upcoming_invoice_count} invoice(s) due soon`);
                return `Attention: ${parts.join(' and ')}.`;
            }
            default:
                return '';
        }
    });

    const statusColor = computed(() => {
        switch (paymentStatus.value) {
            case 'suspended': return '#B71C1C';
            case 'grace': return '#C62828';
            case 'overdue': return '#D32F2F';
            case 'warning': return '#F57F17';
            default: return 'transparent';
        }
    });

    function setStatus(data) {
        status.value = data;
        lastFetched.value = Date.now();
        localStorage.setItem('subscriptionStatus', JSON.stringify(data));
    }

    function fetchStatus() {
        return send('get', 'subscription/status')
            .then((response) => {
                setStatus(response.data.data);
            })
            .catch(() => {});
    }

    function fetchIfStale() {
        const fiveMinutes = 5 * 60 * 1000;
        if (Date.now() - lastFetched.value > fiveMinutes) {
            return fetchStatus();
        }
        return Promise.resolve();
    }

    function updateFromPaymentStatus(paymentStatusStr) {
        if (status.value && paymentStatusStr) {
            status.value = {...status.value, payment_status: paymentStatusStr};
            localStorage.setItem('subscriptionStatus', JSON.stringify(status.value));
        }
    }

    function dismissModal() {
        modalDismissed.value = true;
    }

    function clear() {
        status.value = null;
        lastFetched.value = 0;
        modalDismissed.value = false;
        localStorage.removeItem('subscriptionStatus');
    }

    return {
        status,
        lastFetched,
        modalDismissed,
        paymentStatus,
        isOverdue,
        isWarning,
        needsAttention,
        daysUntilSuspension,
        overdueInvoiceCount,
        overdueTotal,
        expiredDevicesCount,
        expiringDevicesCount,
        statusMessage,
        statusColor,
        setStatus,
        fetchStatus,
        fetchIfStale,
        updateFromPaymentStatus,
        dismissModal,
        clear,
    };
});
